<template>
  <!-- 安全验证弹窗 -->
  <el-dialog
    :visible="visible"
    :title="$t('accountI18n.safeValiate')"
    width="400px"
    :close-on-click-modal="false"
    @close="handleClose"
    append-to-body
  >
    <div class="mag-box">
      <el-radio-group v-model="verifyType" @change="changeType" style="margin-bottom: 10px;">
        <el-radio label="phone">{{ $t('accountI18n.phoneViate') }}</el-radio>
        <el-radio label="email">{{ $t('accountI18n.emailViate') }}</el-radio>
      </el-radio-group>
      <el-form :model="safeForm" :rules="rules" ref="safeFormRef">
        <el-form-item
          v-if="verifyType === 'phone'"
          :label="$t('accountI18n.phone')"
          prop="phone"
        >
          <el-input
            v-model="safeForm.phone"
            :placeholder="$t('accountI18n.phonePlaceholder')"
            maxlength="11"
            autocomplete="off"
            :disabled="isPhoneLocked"
          />
        </el-form-item>
        <el-form-item
          v-if="verifyType === 'email'"
          :label="$t('accountI18n.email')"
          prop="email"
        >
          <el-input
            v-model="safeForm.email"
            :placeholder="$t('accountI18n.emailPlaceholder')"
            autocomplete="off"
            :disabled="isEmailLocked"
          />
        </el-form-item>
        <el-form-item :label="$t('accountI18n.code')" prop="code">
          <div class="justify-between">
            <el-input
              v-model="safeForm.code"
              :placeholder="$t('accountI18n.codePlaceholder')"
              maxlength="6"
            />
            <el-button
              :disabled="countdown > 0 || !canSendCode"
              @click="sendCode"
              style="margin-left: 10px;"
            >
              {{ countdown > 0 ? `${countdown}s ${$t('accountI18n.reSendCode')}` : $t('accountI18n.codeSend') }}
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">{{ $t('accountI18n.cancel') }}</el-button>
      <el-button type="primary" @click="submitCode">{{ $t('accountI18n.confirm') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapState } from "vuex";
import { sendVerifyCode, verifyEmail, sendEmailCode, verifyPhone } from "@/api/index";

const PHONE_REG = /^1\d{10}$/;
const EMAIL_REG = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export default {
  name: "SafeValiate",
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    // true: 已登录改绑，锁定已有手机号/邮箱；false: 忘记密码等，允许手动输入
    isContactLocked: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const phoneValidator = (rule, value, callback) => {
      if (!value || !PHONE_REG.test(value)) {
        callback(new Error(this.$t("accountI18n.phoneError")));
      } else {
        callback();
      }
    };
    const emailValidator = (rule, value, callback) => {
      if (!value || !EMAIL_REG.test(value)) {
        callback(new Error(this.$t("accountI18n.emailError")));
      } else {
        callback();
      }
    };
    const codeValidator = (rule, value, callback) => {
      if (!value || value.length !== 6) {
        callback(new Error(this.$t("accountI18n.codeError")));
      } else {
        callback();
      }
    };
    return {
      safeForm: {
        phone: "",
        email: "",
        code: ""
      },
      verifyType: "phone",
      countdown: 0,
      timer: null,
      rules: {
        phone: [{ validator: phoneValidator, trigger: "blur" }],
        email: [{ validator: emailValidator, trigger: "blur" }],
        code: [{ validator: codeValidator, trigger: "blur" }]
      }
    };
  },
  computed: {
    ...mapState(["userInfo"]),
    // 是否锁定手机号
    isPhoneLocked() {
      return this.isContactLocked && !!this.userInfo?.phone;
    },
    // 是否锁定邮箱
    isEmailLocked() {
      return this.isContactLocked && !!this.userInfo?.email;
    },
    // 是否可以发送验证码
    canSendCode() {
      if (this.verifyType === "phone") {
        return PHONE_REG.test(this.safeForm.phone || "");
      }
      return EMAIL_REG.test(this.safeForm.email || "");
    },
    // 当前联系人
    currentContact() {
      return this.verifyType === "phone" ? this.safeForm.phone : this.safeForm.email;
    }
  },
  watch: {
    // 监听弹窗是否显示
    visible(val) {
      if (val) {
        this.initForm();
      } else {
        this.clearTimer();
        this.countdown = 0;
      }
    }
  },
  methods: {
    // 初始化表单
    initForm() {
      this.verifyType = "phone";
      this.safeForm.phone = this.isContactLocked ? this.userInfo?.phone || "" : "";
      this.safeForm.email = this.isContactLocked ? this.userInfo?.email || "" : "";
      this.safeForm.code = "";
      this.clearTimer();
      this.countdown = 0;
      this.$nextTick(() => {
        this.$refs.safeFormRef && this.$refs.safeFormRef.clearValidate();
      });
    },
    // 切换验证类型
    changeType() {
      if (this.verifyType === "phone") {
        this.safeForm.phone = this.isContactLocked ? this.userInfo?.phone || "" : this.safeForm.phone;
      } else {
        this.safeForm.email = this.isContactLocked ? this.userInfo?.email || "" : this.safeForm.email;
      }
      this.safeForm.code = "";
      if (document.activeElement) {
        document.activeElement.blur();
      }
      this.clearTimer();
      this.countdown = 0;
      this.$nextTick(() => {
        this.$refs.safeFormRef && this.$refs.safeFormRef.clearValidate();
      });
    },
    // 开始倒计时
    startCountdown() {
      this.countdown = 60;
      this.clearTimer();
      this.timer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          this.clearTimer();
        }
      }, 1000);
    },
    // 清除倒计时
    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    // 发送验证码
    sendCode() {
      if (!this.canSendCode || this.countdown > 0) return;
      const field = this.verifyType === "phone" ? "phone" : "email";
      this.$refs.safeFormRef.validateField(field, errorMessage => {
        if (errorMessage) return;
        const request =
          this.verifyType === "phone"
            ? sendVerifyCode({ phone: this.safeForm.phone })
            : sendEmailCode({ email: this.safeForm.email });
        request
          .then(res => {
            if (res.data.code == 200) {
              this.startCountdown();
              this.$message.success(this.$t("accountI18n.codeSendSuccess"));
            } else {
              this.$message.error(res.data.data || this.$t("accountI18n.codeSendError"));
            }
          })
          .catch(err => {
            this.$message.error(err || this.$t("accountI18n.codeSendError"));
          });
      });
    },
    // 提交验证码
    submitCode() {
      this.$refs.safeFormRef.validate(valid => {
        if (!valid) return;
        const request =
          this.verifyType === "phone"
            ? verifyPhone(this.safeForm.phone, { security_code: this.safeForm.code })
            : verifyEmail(this.safeForm.email, { security_code: this.safeForm.code });
        request
          .then(res => {
            if (res.data.code == 200) {
              this.$emit("success", {
                verifyType: this.verifyType,
                validation: res.data.data.validation,
                contact: this.currentContact
              });
              this.$emit("update:visible", false);
              this.reset();
            } else {
              this.$message.error(res.data.data);
            }
          })
          .catch(err => {
            this.$message.error(err || this.$t("accountI18n.codeSendError"));
          });
      });
    },
    // 关闭弹窗
    handleClose() {
      this.$emit("update:visible", false);
      this.reset();
    },
    // 重置表单
    reset() {
      this.clearTimer();
      this.countdown = 0;
      this.safeForm = { phone: "", email: "", code: "" };
      this.verifyType = "phone";
      this.$nextTick(() => {
        this.$refs.safeFormRef && this.$refs.safeFormRef.clearValidate();
      });
    }
  },
  // 销毁前清除倒计时
  beforeDestroy() {
    this.clearTimer();
  }
};
</script>
<style>
.mag-box .el-radio__inner {
  width: 18px !important;
  height: 18px !important;
}

.mag-box .el-radio__label {
  font-size: 18px !important;
}

.mag-box .el-radio__input {
  vertical-align: text-top !important;
}
.mag-box .el-form-item__label {
  font-size: 18px !important;
}

.mag-box .el-input__inner {
  font-size: 18px !important;
}

.mag-box .el-button .el-button--default {
  font-size: 18px !important;
}

.mag-box .el-button .el-button--primary {
  font-size: 18px !important;
}

.el-button {
  font-size: 18px !important;
}
</style>
